import { useState, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Checkbox,
  VStack,
  HStack,
  Text,
} from '@chakra-ui/react';

const PRINT_OPTIONS = [
  { key: 'underwriting', label: 'Underwriting' },
  { key: 'premium_summary', label: 'Premium Summary' },
  {
    key: 'premium_summary_consumer',
    label: 'Premium Summary – Consumer Friendly',
  },
  { key: 'carrier_details', label: 'Carrier Details' },
  {
    key: 'carrier_details_consumer',
    label: 'Carrier Details – Consumer Friendly',
  },
  { key: 'glossary', label: 'Glossary of Terms' },
] as const;

type PrintOptionKey = (typeof PRINT_OPTIONS)[number]['key'];

interface PrintOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPrint: (selectedOptions: PrintOptionKey[]) => void;
  onEmail?: (selectedOptions: PrintOptionKey[]) => void;
  onSaveToPdf?: (selectedOptions: PrintOptionKey[]) => void;
  isLoading?: boolean;
}

const PrintOptionsModal = ({
  isOpen,
  onClose,
  onPrint,
  onEmail,
  onSaveToPdf,
  isLoading = false,
}: PrintOptionsModalProps) => {
  const [selectedOptions, setSelectedOptions] = useState<Set<PrintOptionKey>>(
    new Set(),
  );

  const allSelected = selectedOptions.size === PRINT_OPTIONS.length;
  const noneSelected = selectedOptions.size === 0;

  const handleToggleOption = useCallback((key: PrintOptionKey) => {
    setSelectedOptions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const handleToggleAll = useCallback(() => {
    if (allSelected) {
      setSelectedOptions(new Set());
    } else {
      setSelectedOptions(new Set(PRINT_OPTIONS.map((opt) => opt.key)));
    }
  }, [allSelected]);

  const handlePrint = useCallback(() => {
    onPrint(Array.from(selectedOptions));
  }, [onPrint, selectedOptions]);

  const handleEmail = useCallback(() => {
    onEmail?.(Array.from(selectedOptions));
  }, [onEmail, selectedOptions]);

  const handleSaveToPdf = useCallback(() => {
    onSaveToPdf?.(Array.from(selectedOptions));
  }, [onSaveToPdf, selectedOptions]);

  const handleClose = useCallback(() => {
    setSelectedOptions(new Set());
    onClose();
  }, [onClose]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Print Options</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text fontSize="sm" color="gray.600">
              Select the components you want to include in the print output:
            </Text>

            <HStack justify="flex-end">
              <Button size="xs" variant="link" onClick={handleToggleAll}>
                {allSelected ? 'Deselect All' : 'Select All'}
              </Button>
            </HStack>

            <VStack spacing={3} align="stretch">
              {PRINT_OPTIONS.map((option) => (
                <Checkbox
                  key={option.key}
                  isChecked={selectedOptions.has(option.key)}
                  onChange={() => handleToggleOption(option.key)}
                >
                  {option.label}
                </Checkbox>
              ))}
            </VStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            mr="auto"
            onClick={handleClose}
            isDisabled={isLoading}
          >
            Cancel
          </Button>
          <HStack spacing={2}>
            <Button
              colorScheme="yellow"
              onClick={handleEmail}
              isLoading={isLoading}
              isDisabled={noneSelected}
            >
              Email
            </Button>
            <Button
              colorScheme="yellow"
              onClick={handleSaveToPdf}
              isLoading={isLoading}
              isDisabled={noneSelected}
            >
              Save to PDF
            </Button>
            <Button
              colorScheme="yellow"
              onClick={handlePrint}
              isLoading={isLoading}
              isDisabled={noneSelected}
            >
              Print
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PrintOptionsModal;
export { PRINT_OPTIONS };
export type { PrintOptionKey };
